import { z } from 'zod';
/**
 * La portada de Marketing: el dashboard y las analíticas, en una sola pantalla.
 *
 * ☠️ POR QUÉ LA LECTURA SE PUBLICA COMO UN RANGO Y NO COMO UN PORCENTAJE.
 *
 * La tasa de apertura por píxel es estructuralmente falsa en las DOS
 * direcciones: al alza porque Apple Mail Privacy Protection y el proxy de
 * imágenes de Gmail descargan el píxel sin que nadie haya leído nada, y a la
 * baja porque quien bloquea las imágenes lee el correo entero sin contar. Un
 * «72 % de apertura» es un número inventado con pinta de dato.
 *
 * Así que no se publica uno: se publican los dos extremos de lo que de verdad
 * se sabe. El suelo son las personas que hicieron algo que solo hace una
 * persona —pulsar un enlace—; el techo, las que dispararon el píxel por el
 * motivo que sea. La verdad está entre medias y se dice así.
 */
/** Cuántos envíos hacen falta para que un porcentaje signifique algo. */
export const MIN_ENVIADOS_PARA_ORDENAR = 10;
/**
 * Cuánto tiempo después de recibir una campaña se le atribuye a esa campaña que
 * alguien abra una oportunidad.
 *
 * ☠️ Es una VENTANA TEMPORAL, no una relación causa-efecto. Que alguien pidiera
 * presupuesto tres semanas después de recibir un boletín no significa que el
 * boletín lo trajera: puede haber llamado por otra cosa. Se enseña como «gente
 * que recibió y luego abrió», nunca como «campañas que generaron X euros».
 */
export const DIAS_ATRIBUCION = 90;
export const MarketingOverviewKpisSchema = z.object({
    /** Correos que salieron de verdad en el periodo. */
    enviados: z.number().int(),
    /** Los que el servidor rechazó. */
    fallidos: z.number().int(),
    /**
     * El SUELO de lo leído: personas que pulsaron un enlace. Nadie pulsa un
     * enlace por accidente, así que estos lo leyeron seguro.
     */
    lecturaMinima: z.number().int(),
    /**
     * El TECHO: personas que dispararon el píxel. Incluye a los proxies de
     * imágenes, que lo disparan sin que nadie mire el correo.
     */
    lecturaMaxima: z.number().int(),
    /** Bajas nuevas en el periodo, del motivo que sean. */
    bajasNuevas: z.number().int(),
});
export const MarketingOverviewCampanaSchema = z.object({
    id: z.string(),
    name: z.string(),
    status: z.string(),
    enviados: z.number().int(),
    abiertos: z.number().int(),
    clicados: z.number().int(),
    fallidos: z.number().int(),
    finishedAt: z.string().nullable(),
});
/**
 * Una persona que ha pulsado algo. Es la pieza central de la pantalla: con tres
 * campañas al mes y quinientos contactos, la analítica útil no es un gráfico,
 * es una lista de a quién llamar.
 */
export const MarketingClicRecienteSchema = z.object({
    nombre: z.string(),
    email: z.string(),
    clientId: z.string().nullable(),
    campana: z.string(),
    campanaId: z.string(),
    /** Solo el dominio: la URL entera no cabe y no aporta. */
    destino: z.string(),
    cuando: z.string(),
});
/** Un día de la serie. */
export const MarketingDiaSchema = z.object({
    dia: z.string(),
    enviados: z.number().int(),
    abiertos: z.number().int(),
    clicados: z.number().int(),
});
/**
 * ¿Esto trae trabajo?
 *
 * La única pregunta que justifica hacer marketing, y la única que un Mailchimp
 * no puede contestar porque no sabe nada de tus oportunidades.
 */
export const MarketingAtribucionSchema = z.object({
    /** Oportunidades abiertas por gente que recibió una campaña antes. */
    oportunidades: z.number().int(),
    /** De ellas, las de gente que además pulsó algo. */
    oportunidadesConInteres: z.number().int(),
    /** Lo que suman esas oportunidades, en céntimos. */
    pipelineCents: z.number().int(),
    /** Y lo que de esas se ha llegado a facturar. */
    facturadoCents: z.number().int(),
});
export const MarketingOverviewResponseSchema = z.object({
    desde: z.string(),
    hasta: z.string(),
    dias: z.number().int(),
    kpis: MarketingOverviewKpisSchema,
    campanas: z.array(MarketingOverviewCampanaSchema),
    clicsRecientes: z.array(MarketingClicRecienteSchema),
    serie: z.array(MarketingDiaSchema),
    atribucion: MarketingAtribucionSchema,
    /**
     * Cuentas por las que han salido campañas y por las que NO se leen los
     * rebotes. Un sistema ciego que calla es lo que deja una lista llena de
     * direcciones muertas gastando el cupo de Google.
     */
    remitentesCiegos: z.array(z.string()),
});
//# sourceMappingURL=marketing-stats.js.map